import { useState } from 'react';
import { X } from 'lucide-react';
import type { LabTestCollectionMethodT } from '@lotushealth/lotushealth_js_sdk/schemas';

import { Button } from '@/shared/ui/shadcn/button';
import { Input } from '@/shared/ui/shadcn/input';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/shadcn/select';

import type { MarkerSelectionModel } from '../../model/useMarkerSelection.js';
import { MarkerSearchSheet } from '../selectors/MarkerSearchSheet.js';

const METHODS: Array<{ value: LabTestCollectionMethodT; label: string }> = [
  { value: 'walk_in_test', label: 'Walk-in Test' },
  { value: 'testkit', label: 'Test Kit' },
  { value: 'at_home_phlebotomy', label: 'At-Home Phlebotomy' },
];

export interface CustomTestStepProps {
  name: string;
  onNameChange: (name: string) => void;
  description: string;
  onDescriptionChange: (description: string) => void;
  method: LabTestCollectionMethodT;
  onMethodChange: (method: LabTestCollectionMethodT) => void;
  markerSelection: MarkerSelectionModel;
}

export function CustomTestStep({
  name,
  onNameChange,
  description,
  onDescriptionChange,
  method,
  onMethodChange,
  markerSelection,
}: CustomTestStepProps) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const { selectedMarkers, selectedLabId, addMarker, removeMarker } = markerSelection;

  return (
    <div className="space-y-4">
      <Input
        placeholder="Test name *"
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
      />

      <Textarea
        placeholder="Description *"
        value={description}
        onChange={(e) => onDescriptionChange(e.target.value)}
        rows={2}
      />

      <Select value={method} onValueChange={(v) => onMethodChange(v as LabTestCollectionMethodT)}>
        <SelectTrigger className="max-w-xs">
          <SelectValue placeholder="Collection method" />
        </SelectTrigger>
        <SelectContent>
          {METHODS.map((m) => (
            <SelectItem key={m.value} value={m.value}>
              {m.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">
          Biomarkers{' '}
          {selectedLabId && (
            <span className="font-normal text-muted-foreground">({selectedLabId})</span>
          )}
        </span>
        <Button type="button" variant="secondary" size="sm" onClick={() => setSheetOpen(true)}>
          Add
        </Button>
      </div>

      {selectedMarkers.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground">No biomarkers selected</p>
      ) : (
        <ul className="space-y-1">
          {selectedMarkers.map((m) => (
            <li key={m.id} className="flex items-center justify-between rounded border px-2 py-1">
              <span className="truncate text-sm">{m.name}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeMarker(m.id)}
                aria-label={`Remove ${m.name}`}
              >
                <X className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <MarkerSearchSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onAddMarker={addMarker}
        selectedLabId={selectedLabId}
      />
    </div>
  );
}
